import { QueryParams, TAG_TYPES } from 'types';
import { baseApi } from './index';
import { InventoryDocument, InventoryDocument as T } from 'models';
import { getQueryParams } from 'utils';
import { BaseInventoryDocument, InventoryDocumentResponse } from 'models/InventoryDocument';

const urlEndpoint = `${process.env.REACT_APP_BASE_URL}/inventory-documents`;
const tagType = TAG_TYPES.ReturnOrder;
const ReturnOrderApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllReturnOrders: builder.query<
      {
        data: {
          rows: T[];
          count: number;
          kpis: {
            poReceived: number;
            poReady: number;
            poOverdue: number;
            poCancelled: number;
          };
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query);
        return {
          url: `${urlEndpoint}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [...(result?.data.rows || []).map(({ id }) => ({ type: tagType, id })), { type: tagType, id: 'LIST' }]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),

    getReturnOrderById: builder.query<
      {
        data: InventoryDocument;
        success: boolean;
      },
      { id: string; query: QueryParams }
    >({
      query: (params) => {
        const queryString = params.query ? `?${getQueryParams(params.query)}` : '';
        return {
          url: `${urlEndpoint}/${params.id}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, params) => [{ type: tagType, id: params.id }],
    }),

    createReturnOrder: builder.mutation<InventoryDocumentResponse, Omit<T, 'id'>>({
      query: (data) => ({
        url: urlEndpoint,
        method: 'POST',
        data: {
          ...data,
          type: 'return-purchase-order',
          isPublishing: false,
        },
      }),
      invalidatesTags: [{ type: tagType, id: 'LIST' }],
    }),

    updateReturnOrder: builder.mutation<InventoryDocumentResponse, { id: string; data: BaseInventoryDocument }>({
      query: ({ id, data }) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.Debt, id: 'LIST' },
      ],
    }),

    deleteReturnOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAllReturnOrdersQuery,
  useLazyGetAllReturnOrdersQuery,
  useGetReturnOrderByIdQuery,
  useLazyGetReturnOrderByIdQuery,
  useCreateReturnOrderMutation,
  useUpdateReturnOrderMutation,
  useDeleteReturnOrderMutation,
} = ReturnOrderApiService;
